/**
 * Auto Q&A — ein Auftrag im Einzelnen (Paket 19, Abschnitt C).
 *
 * Eigene Seite, kein Dialog: Hier wird gelesen, verglichen und verlinkt; ein
 * Modal wäre der bequeme, nicht der richtige Ort.
 *
 * Das Herz ist die Schrittkette (C2) — senkrecht, ein Block je Schritt. Der
 * erste Schritt mit Aufhaltern ist beim Öffnen aufgeklappt, alle anderen zu;
 * gibt es keine Aufhalter, ist alles zu. Wer die Seite öffnet, soll das
 * Problem sehen, nicht danach suchen.
 */

import { useMemo, useState } from "react";
import { Link, useParams } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Titel } from "@/components/entwurf/Titel";
import { Mikrolabel } from "@/components/entwurf/Mikrolabel";
import { Rubrik } from "@/components/entwurf/Rubrik";
import { Zustandsmarke } from "@/components/entwurf/Zustandsmarke";
import { DiffAnsicht } from "@/seiten/projekt/lieferung/DiffAnsicht";
import { api } from "@/lib/api";
import { cx, focusRing } from "@/lib/utils";
import { dauerText } from "@/lib/auto-qa-filter";
import {
  auftragsFarbe,
  auftragsMarke,
  schrittErgebnisText,
  vorZeit,
} from "@/lib/auto-qa-zustand";
import { schrittAnzeigename } from "@/lib/auto-qa-anzeige";

const OFFEN = new Set(["gebremst", "wartet", "laeuft"]);

/** Die Gründe im Klartext, wie C2 sie verlangt — nicht der Code. */
const UEBERSPRUNGEN_TEXT: Record<string, string> = {
  fortsetzung_nicht_erfuellt:
    "Übersprungen — die Fortsetzungsbedingung des vorigen Schritts griff nicht.",
  uebersprungen_budget:
    "Übersprungen — das Markenbudget des Auftrags war aufgebraucht.",
  auftrag_abgebrochen: "Übersprungen — der Auftrag wurde abgebrochen.",
  rezept_fehlt: "Übersprungen — das Rezept dieses Schritts gibt es nicht mehr.",
};

export function AutoQaAuftragDetail() {
  const { auftragId } = useParams({
    from: "/_app/auto-qa/auftraege/$auftragId",
  });
  const queryClient = useQueryClient();
  const [offeneSchritte, setzeOffeneSchritte] = useState<Set<string> | null>(
    null,
  );

  const auftrag = useQuery({
    queryKey: ["cubic", "auto-qa", "auftrag", auftragId],
    queryFn: () => api.autoQa.auftrag.query({ id: auftragId }),
    /* Auf der Detailansicht eines laufenden Auftrags: 3000 ms (B7). */
    refetchInterval: (abfrage) => {
      if (abfrage.state.fetchFailureCount >= 4) return false;
      return abfrage.state.data && OFFEN.has(abfrage.state.data.zustand)
        ? 3_000
        : false;
    },
    refetchIntervalInBackground: false,
  });

  const befunde = useQuery({
    queryKey: ["cubic", "auto-qa", "befunde", auftragId],
    queryFn: () => api.autoQa.befunde.query({ auftragId }),
  });

  const abbrechen = useMutation({
    mutationFn: () =>
      api.autoQa.auftragAbbrechen.mutate({
        id: auftragId,
        grund: "Von Hand abgebrochen",
      }),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: ["cubic", "auto-qa"],
      }),
  });

  const daten = auftrag.data;

  /*
   * Welche Blöcke offen stehen. `null` heißt „noch nicht angefasst" — dann
   * entscheidet der erste Schritt mit Aufhaltern. Sobald jemand klickt, gilt
   * seine Auswahl, und die Seite ordnet nicht mehr dazwischen.
   */
  const vorgabeOffen = useMemo(() => {
    if (!daten) return new Set<string>();
    const mitAufhaltern = daten.schritte.find(
      (s) => s.ergebnis === "aufhalter",
    );
    return mitAufhaltern ? new Set([mitAufhaltern.id]) : new Set<string>();
  }, [daten]);

  const sichtbarOffen = offeneSchritte ?? vorgabeOffen;

  function schalteSchritt(id: string): void {
    const naechste = new Set(sichtbarOffen);
    if (naechste.has(id)) naechste.delete(id);
    else naechste.add(id);
    setzeOffeneSchritte(naechste);
  }

  if (auftrag.isLoading) {
    return <Mikrolabel grad={12}>Lade Auftrag …</Mikrolabel>;
  }
  if (auftrag.isError || !daten) {
    return (
      <div className="max-w-[560px]">
        <Titel>Auftrag nicht erreichbar</Titel>
        <div className="mt-11 border-t border-tinte pt-[22px]">
          <p className="text-[15px] font-light leading-[1.7] text-tinte3">
            Der Auftrag gibt es nicht mehr, oder er liegt in einem Projekt ohne
            Rolle.
          </p>
          <Link to="/auto-qa" search={{}} className="mt-5 inline-block text-[12.5px] text-akzent underline decoration-1 underline-offset-[3px]">
            Zurück zur Übersicht
          </Link>
        </div>
      </div>
    );
  }

  const befundeJeSchritt = new Map<number, typeof befunde.data>();
  for (const b of befunde.data ?? []) {
    const liste = befundeJeSchritt.get(b.schrittReihenfolge) ?? [];
    liste.push(b);
    befundeJeSchritt.set(b.schrittReihenfolge, liste);
  }

  return (
    <div>
      {/* C1 — Kopf */}
      <Link
        to="/auto-qa"
        search={{}}
        className={cx(
          "text-[12px] text-akzent underline decoration-1 underline-offset-[3px]",
          focusRing,
        )}
      >
        ← Auto Q&A
      </Link>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <Zustandsmarke farbe={auftragsFarbe(daten.zustand)}>
          {auftragsMarke(daten.zustand)}
        </Zustandsmarke>
        {daten.probelauf ? (
          <Zustandsmarke farbe="grau">Probelauf</Zustandsmarke>
        ) : null}
        <span className="font-mono text-[12px] text-tinte4">
          {daten.zielArt}
        </span>

        <div className="ml-auto flex items-center gap-3">
          {OFFEN.has(daten.zustand) ? (
            <button
              type="button"
              onClick={() => abbrechen.mutate()}
              disabled={abbrechen.isPending}
              className={cx(
                "text-[12.5px] text-akzent underline decoration-1 underline-offset-[3px] disabled:opacity-50",
                focusRing,
              )}
            >
              {abbrechen.isPending ? "Bricht ab …" : "Abbrechen"}
            </button>
          ) : null}
          {daten.automationId ? (
            <Link
              to="/auto-qa/automationen/$automationId"
              params={{ automationId: daten.automationId }}
              className={cx(
                "text-[12.5px] text-akzent underline decoration-1 underline-offset-[3px]",
                focusRing,
              )}
            >
              Automation öffnen
            </Link>
          ) : null}
        </div>
      </div>

      <div className="mt-3">
        <Titel>
          {daten.probelauf ? "Probelauf" : "Auftrag"} ·{" "}
          {daten.schritte.length}{" "}
          {daten.schritte.length === 1 ? "Schritt" : "Schritte"}
        </Titel>
      </div>

      {daten.probelauf ? (
        <p className="mt-3 max-w-[62ch] text-[13.5px] font-light leading-[1.65] text-tinte3">
          Probelauf — Befunde werden geschrieben, Zustände nicht geändert.
        </p>
      ) : null}

      {daten.abbruchgrund ? (
        <div className="mt-5 border-l-2 border-warn bg-warnflaeche px-3.5 py-2.5">
          <Mikrolabel grad={12} className="text-white">
            {daten.abbruchgrund}
          </Mikrolabel>
        </div>
      ) : null}

      <div className="mt-6 flex flex-wrap gap-x-8 gap-y-2 border-t border-linie pt-4">
        <Mikrolabel grad={11.5}>
          Fassung {daten.automationVersion}
        </Mikrolabel>
        <Mikrolabel grad={11.5} title={new Date(daten.angelegtAm).toLocaleString("de-DE")}>
          angelegt {vorZeit(daten.angelegtAm)}
        </Mikrolabel>
        <Mikrolabel grad={11.5}>Dauer {dauerText(daten.dauerMs)}</Mikrolabel>
        <Mikrolabel grad={11.5}>
          <span className="tabular-nums">
            {daten.markenVerbraucht.toLocaleString("de-DE")}
          </span>{" "}
          Marken
        </Mikrolabel>
        {daten.aufhalter > 0 ? (
          <Mikrolabel grad={11.5} className="text-warn">
            {daten.aufhalter} Aufhalter
          </Mikrolabel>
        ) : null}
      </div>

      {/* C2 — die Schrittkette */}
      <div className="mt-10">
        <Rubrik anzahl={`${daten.schritte.length}`}>Kette</Rubrik>

        {daten.schritte.map((schritt, i) => {
          const offen = sichtbarOffen.has(schritt.id);
          const meine = befundeJeSchritt.get(schritt.reihenfolge) ?? [];
          const uebersprungen = schritt.zustand === "uebersprungen";

          return (
            <div
              key={schritt.id}
              className={cx(
                "border-b border-linie",
                offen ? "bg-wisch" : "",
                uebersprungen ? "opacity-60" : "",
              )}
            >
              <button
                type="button"
                aria-expanded={offen}
                onClick={() => schalteSchritt(schritt.id)}
                className={cx(
                  "flex w-full items-center gap-3.5 px-3 py-3 text-left hover:bg-wisch",
                  focusRing,
                )}
              >
                <span className="flex-none font-mono text-[12px] tabular-nums text-tinte4">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="min-w-0 flex-1">
                  <span
                    className={cx(
                      "block truncate text-[13.5px] text-tinte",
                      uebersprungen ? "line-through" : "",
                    )}
                  >
                    {schrittAnzeigename(schritt)}
                  </span>
                  <span className="mt-0.5 block text-[11.5px] text-tinte4">
                    {uebersprungen && schritt.uebersprungenGrund
                      ? (UEBERSPRUNGEN_TEXT[schritt.uebersprungenGrund] ??
                        schritt.uebersprungenGrund)
                      : `${schrittErgebnisText(schritt.zustand, schritt.ergebnis)} · ${dauerText(schritt.dauerMs)}${meine.length > 0 ? ` · ${meine.length} Befunde` : ""}`}
                  </span>
                </span>
                <span aria-hidden="true" className="flex-none text-[11px] text-tinte4">
                  {offen ? "▾" : "▸"}
                </span>
              </button>

              {offen ? (
                <div className="px-3 pb-4">
                  {schritt.durchsichtLaufId ? (
                    <Link
                      to="/auto-qa/laeufe/$laufId"
                      params={{ laufId: schritt.durchsichtLaufId }}
                      className={cx(
                        "text-[12px] text-akzent underline decoration-1 underline-offset-[3px]",
                        focusRing,
                      )}
                    >
                      Durchsichtlauf öffnen
                    </Link>
                  ) : (
                    <Mikrolabel grad={11.5}>
                      Kein Durchsichtlauf — der Schritt lief nicht.
                    </Mikrolabel>
                  )}

                  {schritt.letzterFehler ? (
                    <div className="mt-3 border-l-2 border-warn bg-warnflaeche px-3.5 py-2.5">
                      <Mikrolabel grad={12}>{schritt.letzterFehler}</Mikrolabel>
                    </div>
                  ) : null}

                  {/* C3 — Befunde */}
                  {meine.length === 0 ? (
                    <Mikrolabel grad={11.5} className="mt-3 block">
                      Keine Befunde an diesem Schritt.
                    </Mikrolabel>
                  ) : (
                    <div className="mt-4">
                      {meine.map((befund) => (
                        <div
                          key={befund.id}
                          id={befund.id}
                          className="border-t border-linie py-3"
                        >
                          <div className="flex flex-wrap items-baseline gap-3">
                            <span
                              className={cx(
                                "flex-none font-mono text-[11px] uppercase",
                                befund.schwere === "kritisch" ||
                                  befund.schwere === "schwer"
                                  ? "text-warn"
                                  : "text-tinte4",
                              )}
                            >
                              {befund.schwere}
                            </span>
                            <span className="flex-none font-mono text-[11.5px] tabular-nums text-tinte3">
                              {befund.datei}
                              {befund.zeile === null ? "" : `:${befund.zeile}`}
                            </span>
                            <span className="ml-auto flex-none text-[11px] text-tinte4">
                              {befund.zustand}
                            </span>
                          </div>
                          <p className="mt-1.5 text-[13.5px] font-light leading-[1.6] text-tinte">
                            {befund.text}
                          </p>
                          {befund.beleg ? (
                            <div className="mt-2.5">
                              <DiffAnsicht diff={befund.beleg} />
                            </div>
                          ) : (
                            <Mikrolabel grad={11} className="mt-2 block">
                              Kein Beleg — der Lauf trägt keinen eingefrorenen
                              Diff.
                            </Mikrolabel>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}
